import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  gDriveContentFilesForSelectedCourseAtom,
  gDriveContentForCourse,
  gDriveFilesForIdAtom,
  gDriveFilesForSelectedCourseAtom,
  GDriveUnitFiles,
  pairedFilesAtom,
  parsedAtom,
  phenomIconMapAtom,
  selectedCourseAtom,
  userMessagesAtom,
} from './atoms'
import { useCallback, useContext } from 'react'
import { htmlDocForUnitUrl } from './utility'
import { getDocMetadata, parseHtmlString, parseUnits } from './html'
import { uploadCourse } from '../../components/ingestion/Utility'
import { PartnerContext } from '../PartnerContext'
import { googleUserAtom, getFilesRecursive2 } from 'infrastructure/googleDrive'
import { IResourceParams, ParsedGoogleDocType } from './types'
import { parseGoogleDoc } from './gDocParser'
import { allNodesForTypePath } from '../nodeTransformationHelpers'
import { ICourseNode, INoteNode } from '../../components/ingestion/IngestedTree'
import { NodeType } from 'shared/types/ingestion'
import { useNoteNodeProcessing } from './noteNodeProcessing'
import { useGetDoc } from '../../components/tree/googleApis/hooks'

export function useParseForKey() {
  const courseId = useAtomValue(selectedCourseAtom)
  const setParsed = useSetAtom(parsedAtom)
  const getParams = useInitialParamsForCourseId()
  const setMessages = useSetAtom(userMessagesAtom)
  const postProcess = usePostTreeProcessing()

  return useCallback(
    async (unitFiles: string[]) => {
      const params = await getParams(unitFiles)
      if (!params) {
        setMessages((prev) => [...prev, 'Cant parse, missing parsing params'])
        return
      }
      try {
        const course = await parseUnits(params)
        postProcess(course, courseId)
        setParsed(course)
        return course
      } catch (e) {
        if (e instanceof Error && e.stack) {
          setMessages([cleanStack(e.stack)])
        } else {
          setMessages([String(e)])
        }
      }
    },
    [courseId, getParams, setParsed, setMessages, postProcess],
  )
}
function cleanStack(stack: string): string {
  return stack
    .split('\n')
    .map((line) =>
      line.replace(
        // Match full URLs ending in file.tsx:line:col or similar
        /https?:\/\/[^)]+\/([^/?\s]+(?:\.(ts|js|tsx|jsx)))(\?[^):]*)?(:\d+:\d+)/g,
        // Replace with just file and line/column info
        (_, file, _ext, _query, position) => `${file}${position}`,
      ),
    )
    .join('\n')
}

function convertActivityNotes(tree: ICourseNode) {
  allNodesForTypePath<INoteNode>(tree, [
    NodeType.Activity,
    NodeType.Note,
  ]).forEach((n) => {
    n.data.studentFacing = true
  })
}

function usePostTreeProcessing() {
  const noteNodeProcessing = useNoteNodeProcessing()
  return useCallback(
    (course: ICourseNode, courseId: string) => {
      noteNodeProcessing(course)
      convertActivityNotes(course)
    },
    [noteNodeProcessing],
  )
}

function useBaseParamsForFileUrls() {
  return useCallback(async (unitFiles: string[]) => {
    const downloaded = (
      await Promise.all(unitFiles.map((url) => htmlDocForUnitUrl(url)))
    ).filter((d) => !!d) as Document[]
    return downloaded.map((htmlContent) => {
      const metadata = getDocMetadata(htmlContent)
      return {
        htmlContent,
        metadata,
      }
    })
  }, [])
}

function useInitialParamsForCourseId() {
  const getBaseParams = useBaseParamsForFileUrls()
  const phenomIconMap = useAtomValue(phenomIconMapAtom)
  const userMessageSetter = useSetAtom(userMessagesAtom)
  const gDriveContentFiles = useAtomValue(
    gDriveContentFilesForSelectedCourseAtom,
  )

  return useCallback(
    async (unitFiles: string[]): Promise<IResourceParams[] | undefined> => {
      if (!gDriveContentFiles) return
      const baseParams = await getBaseParams(unitFiles)
      return baseParams.map((params) => {
        return {
          ...params,
          gDrive: gDriveContentFiles,
          phenomIconMap,
          messageCallback: userMessageSetter,
        }
      })
    },
    [gDriveContentFiles, getBaseParams, phenomIconMap, userMessageSetter],
  )
}

export function useParserForGoogleFile() {
  const { getDoc, canGetDoc } = useGetDoc()

  return useCallback(
    async (fileId: string) => {
      if (!canGetDoc || !fileId) return
      const contend = await getDoc(fileId)
      const doc = parseHtmlString(contend)
      return parseGoogleDoc(doc)
    },
    [getDoc, canGetDoc],
  )
}

export function useUploadForKey() {
  const courseId = useAtomValue(selectedCourseAtom)
  const getParsed = useParseForKey()
  const partnerId = useContext(PartnerContext)
  const setMessages = useSetAtom(userMessagesAtom)

  return useCallback(
    async (unitFiles: string[]) => {
      if (!partnerId) return
      const parsed = await getParsed(unitFiles)
      if (!parsed) {
        setMessages((prev) => [...prev, 'Cant upload, missing parsed tree'])
        return
      }
      return uploadCourse(partnerId, parsed, courseId)
    },
    [getParsed, partnerId, courseId, setMessages],
  )
}

export function usePairedFileForKey() {
  const courseKey = useAtomValue(selectedCourseAtom)
  const pairedFiles = useAtomValue(pairedFilesAtom)
  return pairedFiles[courseKey]
}

export function useRetrieveGDriveContentForCourse() {
  const setContent = useSetAtom(gDriveContentForCourse)
  const files = useAtomValue(gDriveFilesForSelectedCourseAtom)
  const selectedCourse = useAtomValue(selectedCourseAtom)

  const fileParser = useParserForGoogleFile()

  return useCallback(async () => {
    if (!files || !Array.isArray(files)) {
      return
    }
    const parsed = await files.reduce<
      Promise<Record<string, ParsedGoogleDocType>>
    >(async (acc, fileGroup: { fileMap?: GDriveUnitFiles }) => {
      const { fileMap } = fileGroup
      const current = await acc
      if (!fileMap) return current
      await Promise.all(
        Object.values(fileMap).map(async (lessonFiles) => {
          return Promise.all(
            Object.values(lessonFiles).map(async (lessonFile) => {
              const parsed = await fileParser(lessonFile.id)
              if (parsed) {
                const keyParts = lessonFile.name.split('.').slice(0, 3)
                current[keyParts.join('').split(' ')[0]] = parsed
              }
            }),
          )
        }),
      )
      return current
    }, Promise.resolve({}))
    setContent((prev) => {
      return {
        ...prev,
        [selectedCourse]: parsed,
      }
    })
  }, [setContent, fileParser, selectedCourse, files])
}

export function useIsParseEnabled() {
  const courseFiles = useAtomValue(gDriveContentFilesForSelectedCourseAtom)
  return !!courseFiles
}

export function useRetrieveGoogleContent() {
  const [gDriveFiles, setGDriveFiles] = useAtom(gDriveFilesForIdAtom)
  const { credential } = useAtomValue(googleUserAtom)
  return useCallback(
    async (folderId: string) => {
      if (folderId in gDriveFiles || !credential) return
      const files = await getFilesRecursive2(credential.accessToken, folderId)
      setGDriveFiles((prev) => {
        return {
          ...prev,
          [folderId]: files,
        }
      })
    },
    [gDriveFiles, setGDriveFiles, credential],
  )
}
